import React, { useState } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { useHistory, Link } from 'react-router-dom';
import { Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SpeakerNotesOutlinedIcon from '@mui/icons-material/SpeakerNotesOutlined';
import PersonIcon from '@mui/icons-material/Person';
import BlockIcon from '@mui/icons-material/Block';
import { fetchJson } from '#action/fetch';
import { webPortalApi } from '#config/web-portal-api';
import SimpleCardTemplate from '#component/general/simple-card-template';
import ArrowRightButton from '#component/general/arrow-right-button';
import MobileDrawerMenu from '#component/general/mobile-drawer-menu';
import HighlightWord from '#component/general/highlight-word/highlight-word';
import { getHighlightedSearchResult } from '#component/general/highlight-word/helper';
import { ProjectLink, ShowMoreContainer } from '#component/user/dashboard/styles';
import ProjectsModal from './projects-modal';
import DevicesCountButton from './devices-count-button';

const MobileCard = props => {
  const { user } = props;
  const history = useHistory();
  const listFilter = useSelector(s => s.listFilter, shallowEqual);
  const highlightedSearchResult = getHighlightedSearchResult(listFilter);

  const [isDrawerMenuOpen, setIsDrawerMenuOpen] = useState(false);
  const [userProjects, setUserProjects] = useState([]);

  const handleDrawerMenuClose = () => {
    setIsDrawerMenuOpen(false);
  };

  const onCloseProjectsModal = () => {
    setUserProjects([]);
  };

  const handleEditUserClick = () => history.push(`/user/${user.id}`);
  const handleAuditTrailClick = () => history.push(`/user-log?targetUser=${user.id}`);

  const userMenuItems = [
    { name: 'Edit user', onClick: handleEditUserClick, icon: <EditIcon /> },
    { name: 'Audit trail', onClick: handleAuditTrailClick, icon: <SpeakerNotesOutlinedIcon /> }
  ];

  const getUserRoleContent = () => {
    if (user?.userType > 2) {
      return <Typography color="textSecondary" fontWeight={500}>Standard</Typography>;
    } if (user.allowManageAlertAcceptance === 1) {
      return <Typography color="textSecondary" fontWeight={500}>Quality Manager</Typography>;
    }
    return <Typography color="textSecondary" fontWeight={500}>Extended</Typography>;
  };

  return (
    <>
      <SimpleCardTemplate
        onCardClickHeader={() => history.push(`/user/${user.id}`)}
        title={
          highlightedSearchResult ? <HighlightWord
            textToHighlight={`${user.firstName} ${user.lastName}`}
            searchWord={highlightedSearchResult}
          /> : `${user.firstName} ${user.lastName}`
        }
        icon={user.disabled ? <BlockIcon /> : <PersonIcon color="secondary" />}
        cardContents={[
          {
            title: 'User role',
            content: getUserRoleContent()
          },
          {
            title: 'Devices',
            content: (user.devices > 0) ? (
              <DevicesCountButton
                devicesCount={user?.devices}
                project={user?.project}
                user={user}
              />
            ) : (
              <Typography color="textSecondary" fontWeight="bold">No devices</Typography>
            )
          },
          (Array.isArray(user.directProjects) && {
            title: 'Root project',
            content: user.directProjects.length > 1 ? (
              <ShowMoreContainer>
                <Typography color="textSecondary" fontWeight="bold">
                  {user.directProjects.length}
                </Typography>
                <ArrowRightButton onClickArrowRight={
                  async () => {
                    const url = webPortalApi.buildApiUrl(`project?ids=${user.directProjects.join(',')}`);
                    const projects = await fetchJson(url);
                    setUserProjects(projects);
                  }
                }
                />
              </ShowMoreContainer>
            ) : (
              <ProjectLink
                color="secondary"
                component={Link}
                to={`/project/edit/${user.defaultProject.id}`}
              >
                {highlightedSearchResult ? (
                  <div>
                    <HighlightWord
                      textToHighlight={user.defaultProject.title}
                      searchWord={highlightedSearchResult}
                    />
                  </div>
                ) : user.defaultProject.title}
              </ProjectLink>

            )
          }),
          {
            title: 'Email',
            content: highlightedSearchResult ? <HighlightWord
              textToHighlight={user.email}
              searchWord={highlightedSearchResult}
            /> : user.email,
            customStyles: {
              content: {
                maxWidth: '80%',
                wordWrap: 'break-word'
              }
            }
          }
        ]}
        onClickMore={() => setIsDrawerMenuOpen(true)}
      />
      <MobileDrawerMenu
        isOpen={isDrawerMenuOpen}
        onClose={handleDrawerMenuClose}
        menuTitle={`${user.firstName} ${user.lastName}`}
        menuItems={userMenuItems}
      />
      <ProjectsModal
        isOpen={userProjects.length}
        onClose={onCloseProjectsModal}
        projects={userProjects}
      />
    </>
  );
};

export default MobileCard;
